import React, { useEffect, useState } from 'react'
import { cn } from '@duck/ui'
import { PartnerService } from '@duck/api-definitions'

type LogoCanvas = PartnerService.OrganizationLogoCanvas

interface OrgWordmarkProps {
    org: { name: string; logoURL?: string | null; logoCanvas?: LogoCanvas | null }
    className?: string
    /** Render the bare image with no canvas wrapper. Useful when the parent supplies its own framing. */
    noCanvas?: boolean
}

const canvasStyles: Record<LogoCanvas, string> = {
    plaque: 'bg-[#faf9f6] ring-1 ring-black/5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.6)]',
    obsidian: 'bg-neutral-900 ring-1 ring-white/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)]',
    vellum: 'bg-foreground/[0.04] ring-1 ring-foreground/10 backdrop-blur-sm',
}

export const OrgWordmark: React.FC<OrgWordmarkProps> = ({
    org,
    className = '',
    noCanvas = false,
}) => {
    const logo = org.logoURL
    const [imageError, setImageError] = useState(false)

    useEffect(() => {
        setImageError(false)
    }, [logo])

    if (!logo || imageError) {
        return null
    }

    const img = (
        <img
            src={logo}
            alt={org.name}
            onError={() => setImageError(true)}
            className={cn(
                'max-h-full max-w-full object-contain',
                noCanvas && 'max-h-20 max-w-60 mx-auto mb-4',
                noCanvas && className,
            )}
        />
    )

    if (noCanvas) {
        return img
    }

    const canvas = canvasStyles[org.logoCanvas ?? 'plaque']

    return (
        <div
            className={cn(
                'inline-flex items-center justify-center rounded-md px-3 py-1.5',
                'max-h-20 max-w-60 mx-auto mb-4',
                canvas,
                className,
            )}
        >
            {img}
        </div>
    )
}
